import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import WorkOrderForm from '../components/WorkOrderForm';
import HoursLogger from '../components/HoursLogger';
import Fold from '../components/Fold';
import TicketExpenses from '../components/TicketExpenses';
import TicketAppointments from '../components/TicketAppointments';
import { Dialog } from '../components/todos/TodoPeople';
import { contractorToken } from '../lib/contractorAuth';
import './WorkOrders.css';
import './WorkOrderDetail.css';

const WorkOrderDetail = ({ isAdmin }) => {
  // The contractor's own credential, when there is one.
  //
  // lib/apiClient attaches only the STAFF token, so this page sent no
  // credential at all for a contractor - and App.js routes /work-orders/:id
  // for them, with WorkOrdersList drawing the link. Every request answered 401:
  // a page the app invites a contractor into that could not load for them.
  // Undefined for staff, so their requests go through the interceptor exactly
  // as before.
  // Memoised on the token itself: contractorAuth() builds a fresh object every
  // call, and a fresh object in a dependency array re-runs the effect on every
  // render, which is an endless refetch rather than a working page.
  const token = contractorToken();
  const auth = useMemo(
    () => (token ? { headers: { Authorization: `Bearer ${token}` } } : undefined),
    [token]);
  const { id } = useParams();
  const navigate = useNavigate();
  const [workOrder, setWorkOrder] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [hoursList, setHoursList] = useState([]);
  // The server's own view of this ticket's time: its stored logged_hours, the
  // allocation, and whether more time can go on. Kept whole rather than picked
  // apart, so the figure shown here is the figure the Work Orders list shows
  // rather than this page's own arithmetic on a list it happened to fetch.
  const [timesheet, setTimesheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The call-off dialog: open, the reason typed, what the server said, busy.
  const [callOffOpen, setCallOffOpen] = useState(false);
  const [callOffReason, setCallOffReason] = useState('');
  const [callOffError, setCallOffError] = useState(null);
  const [callingOff, setCallingOff] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Fetch work order, ticket, and hours data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch work order
        const woResponse = await axios.get(
          `${API_BASE}/work-orders/${id}`, auth
        );

        // THE WORK ORDER IS THE TICKET'S "WORK & COSTS" TAB now (Gordon,
        // ticket page redesign, decision 2): its hours, budget, expenses and
        // visits live there, with Call off and Edit for an administrator. A
        // work order on a ticket goes there; the old address keeps working.
        // One raised without a ticket has nowhere else to be, so it keeps
        // this page.
        if (woResponse.data?.ticket_id) {
          navigate(`${isAdmin ? '/tickets' : '/my-tickets'}/${woResponse.data.ticket_id}/work`, { replace: true });
          return;
        }
        setWorkOrder(woResponse.data);

        // Fetch linked ticket if ticket_id exists
        if (woResponse.data.ticket_id) {
          try {
            const ticketResponse = await axios.get(
              `${API_BASE}/tickets/${woResponse.data.ticket_id}`, auth
            );
            setTicket(ticketResponse.data);
          } catch (err) {
            console.error('Error fetching ticket:', err);
          }
        }
        
        // The hours on THIS job.
        //
        // This used to call GET /api/hours-logs?work_order_id=<id>, and that
        // endpoint takes no filter - no WHERE clause at all, 500-row limit,
        // every hours_logs row in the database. So every work order detail
        // page showed the whole firm's time and totalled it: three jobs
        // holding 7h, 4h and 2h made all three pages read 13h logged, with
        // "Remaining -13h" against a 0h allocation. The query string was
        // ignored and nothing said so.
        //
        // GET /api/tickets/:id/time is the endpoint that owns this. It
        // returns the entries for one ticket with the work order's own stored
        // total, and it is gated by ticketAccess - an administrator, the
        // contractor the ticket names, or an internal worker - where
        // /hours-logs is requireAdmin. That second part matters: threading a
        // contractor token through this page was not enough on its own,
        // because the endpoint it was pointed at would have refused them
        // whatever they sent.
        if (woResponse.data.ticket_id) {
          try {
            const sheet = await axios.get(
              `${API_BASE}/tickets/${woResponse.data.ticket_id}/time`, auth
            );
            setHoursList(Array.isArray(sheet.data?.entries) ? sheet.data.entries : []);
            // Only the object shape; an array here is not a timesheet.
            setTimesheet(sheet.data && !Array.isArray(sheet.data) ? sheet.data : null);
          } catch (err) {
            console.error('Error fetching time on this ticket:', err);
          }
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching work order:', err);
        setError('Failed to load work order details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, API_BASE, auth, isAdmin, navigate]);

  const handleSaveWorkOrder = (updatedData) => {
    setWorkOrder(updatedData);
  };

  const handleHoursLogged = () => {
    // Refresh hours list when new hours are logged
    const fetchHours = async () => {
      try {
        // Also refresh the work order, because its stored logged_hours is what
        // the list reads and what the allocation is measured against.
        const woResponse = await axios.get(
          `${API_BASE}/work-orders/${id}`, auth
        );
        setWorkOrder(woResponse.data);

        if (woResponse.data.ticket_id) {
          const sheet = await axios.get(
            `${API_BASE}/tickets/${woResponse.data.ticket_id}/time`, auth
          );
          setHoursList(Array.isArray(sheet.data?.entries) ? sheet.data.entries : []);
          setTimesheet(sheet.data && !Array.isArray(sheet.data) ? sheet.data : null);
        }
      } catch (err) {
        console.error('Error refreshing hours:', err);
      }
    };
    fetchHours();
  };

  // Calling the job off.
  //
  // This was a "Delete" button that sent DELETE /work-orders/:id - a route
  // that has never existed, so every press fell to the 404 handler and the
  // page said "Failed to delete work order" about a job that was still there.
  // The server's verb is POST /work-orders/:id/cancel, and it wants a reason:
  // a job called off with no reason is the thing somebody finds in six months
  // and cannot explain. It also refuses a job with hours logged against it
  // (409, naming the hours), because work that was done is approved or
  // disputed, not cancelled. Both refusals are shown in the dialog, in the
  // server's own words, rather than as "failed".
  const callOff = async () => {
    const reason = callOffReason.trim();
    if (!reason) {
      setCallOffError('Say why the job was called off');
      return;
    }
    setCallingOff(true);
    setCallOffError(null);
    try {
      await axios.post(`${API_BASE}/work-orders/${id}/cancel`, { reason }, auth);
      setCallOffOpen(false);
      setCallOffReason('');
      // Read it back rather than guessing: the notes now carry the reason and
      // the status is the server's to say.
      const woResponse = await axios.get(`${API_BASE}/work-orders/${id}`, auth);
      setWorkOrder(woResponse.data);
    } catch (err) {
      console.error('Error calling off work order:', err);
      const body = err.response?.data;
      setCallOffError(body?.error
        ? body.detail ? `${body.error}. ${body.detail}` : body.error
        : 'Could not call that job off');
    } finally {
      setCallingOff(false);
    }
  };

  if (loading) {
    return (
      <div className="work-orders-page detail-section" style={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        Loading work order details...
      </div>
    );
  }

  if (error) {
    return (
      <div className="work-orders-page work-orders-error" style={{ margin: '20px' }}>
        {error}
      </div>
    );
  }

  if (!workOrder) {
    return (
      <div className="work-orders-page work-orders-error" style={{ margin: '20px' }}>
        Work order not found
      </div>
    );
  }

  // The stored total, which is what the Work Orders list shows and what an
  // invoice is built on - not this page's own sum of whatever rows it fetched.
  // Those two disagreed in production, and the page was the one that was
  // wrong. Summing the entries is the fallback for when the timesheet read
  // failed, so a broken read shows the hours it did get rather than zero.
  const loggedHours = timesheet && timesheet.logged_hours != null
    ? Number(timesheet.logged_hours)
    : hoursList.reduce((sum, entry) => sum + parseFloat(entry.hours_logged || 0), 0);
  const allocated = parseFloat(workOrder.allocated_hours || 0);
  const remainingHours = allocated - loggedHours;
  // No allocation is its own state, not 0%.
  //
  // This read `allocated > 0 ? ... : 0`, which printed "Used 0%" on a job with
  // 13 hours logged against no allocation - and "nothing used" is the opposite
  // of what was happening. The list page had the matching bug in the other
  // direction and printed "Infinity%". There is no percentage of nothing, so
  // neither figure is reported: the row says so instead.
  const noAllocation = !(allocated > 0);
  const utilizationPercent = noAllocation ? null : (loggedHours / allocated) * 100;

  const barState = noAllocation ? 'none'
    : utilizationPercent > 100 ? 'danger'
    : utilizationPercent > 90 ? 'warning' : 'ok';
  const remainingTone = noAllocation ? undefined
    : remainingHours < 0 ? 'over'
    : utilizationPercent > 90 ? 'tight' : undefined;

  // The status column holds an enum, and the page printed it raw and
  // uppercased: IN_PROGRESS, ON_HOLD. Status names people recognise are what
  // every other page in Jarvis shows.
  const statusWord = (s) => String(s || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase());

  const hours = (n) => `${Number(n).toFixed(2).replace(/\.?0+$/, '')}h`;
  const day = (d) => (d
    ? new Date(d).toLocaleDateString('en-GB', { timeZone: 'Europe/Malta', day: 'numeric', month: 'short', year: 'numeric' })
    : null);

  return (
    /* work-orders-page stays on the root: WorkOrders.css scopes the status
       badges, the hours logger and the form's own styles to it, so dropping
       it left the status reading as plain text. */
    <div className="wo-page work-orders-page work-order-detail">
      <div className="wo-topline">
        <button
          onClick={() => navigate(isAdmin ? '/work-orders' : '/my-work-orders')}
          className="wo-back"
        >
          ← Back
        </button>
        <span className="wo-number">
          {workOrder.work_order_number || `WO-${workOrder.id}`}
        </span>
        <span className={`status-badge status-${workOrder.status}`}>
          {statusWord(workOrder.status)}
        </span>
        {/* Only a job still open can be called off: the server refuses one
            already completed or cancelled, so the button is not offered. */}
        {isAdmin && workOrder.status !== 'completed' && workOrder.status !== 'cancelled' && (
          <button
            onClick={() => { setCallOffOpen(true); setCallOffError(null); }}
            className="wo-danger"
          >
            Call off
          </button>
        )}
      </div>

      {callOffOpen && (
        <Dialog
          label={`Call off ${workOrder.work_order_number || `WO-${workOrder.id}`}?`}
          onClose={() => { if (!callingOff) setCallOffOpen(false); }}
          className="wo-calloff"
        >
          <p className="tds-quiet">
            The job is marked cancelled and the reason is kept on it and on the
            ticket&rsquo;s timeline. A job with hours logged against it cannot be
            called off.
          </p>
          <label className="tds-field">
            <span>Why is it being called off?</span>
            <textarea
              rows="3"
              value={callOffReason}
              onChange={(e) => setCallOffReason(e.target.value)}
              disabled={callingOff}
              placeholder="e.g. The client withdrew the request"
            />
          </label>
          {callOffError && <p className="tds-error" role="alert">{callOffError}</p>}
          <div className="tds-dialog-foot">
            <button type="button" className="btn btn-outline tds-btn" disabled={callingOff}
              onClick={() => setCallOffOpen(false)}>Keep it</button>
            <button type="button" className="btn btn-danger tds-btn wo-calloff-confirm"
              disabled={callingOff || !callOffReason.trim()} onClick={callOff}>
              {callingOff ? 'Calling off…' : 'Call off the job'}
            </button>
          </div>
        </Dialog>
      )}

      {/* The job, the hours it is measured against, and who is doing it. The
          allocated figure used to be printed twice - once in the work order
          card and again under "Hours Tracking" two sections below - and the
          four hours figures each had an uppercase label above a 2rem number
          in a column of its own, 162px for four numbers. */}
      <div className="wo-card">
        <h1 className="wo-title">{workOrder.description}</h1>

        <dl className="wo-hours">
          <div className="wo-hour">
            <dt>Allocated</dt>
            <dd>{noAllocation ? 'None' : hours(allocated)}</dd>
          </div>
          <div className="wo-hour" data-tone="logged">
            <dt>Logged</dt><dd>{hours(loggedHours)}</dd>
          </div>
          {/* Remaining and Used are both answers to "against the allocation",
              so with no allocation neither has one. Showing "-13h remaining"
              of nothing, and 0% of nothing, were two ways of saying something
              untrue about the same job. */}
          <div className="wo-hour" data-tone={remainingTone}>
            <dt>Remaining</dt>
            <dd>{noAllocation ? '—' : hours(remainingHours)}</dd>
          </div>
          <div className="wo-hour">
            <dt>Used</dt>
            <dd>{noAllocation ? '—' : `${utilizationPercent.toFixed(0)}%`}</dd>
          </div>
          {/* Under the whole row, not under the percentage - it is about all
              four figures, and sitting in the fourth column it read as part
              of that one. */}
          <div
            className="wo-bar"
            data-state={barState}
            role="img"
            aria-label={noAllocation
              ? `${hours(loggedHours)} logged against no allocation`
              : `${utilizationPercent.toFixed(0)}% of the allocated hours used`}
          >
            <span style={{ width: noAllocation
              ? '0%' : `${Math.min(utilizationPercent, 100)}%` }} />
          </div>
        </dl>

        <dl className="wo-meta">
          {workOrder.contractor_name && (
            <div className="wo-fact">
              <dt>Contractor</dt><dd>{workOrder.contractor_name}</dd>
            </div>
          )}
          {workOrder.hourly_rate != null && workOrder.hourly_rate !== '' && (
            <div className="wo-fact">
              <dt>Rate</dt>
              <dd>€{Number(workOrder.hourly_rate).toFixed(2)}/hr</dd>
            </div>
          )}
          {day(workOrder.start_date) && (
            <div className="wo-fact">
              <dt>Starts</dt><dd>{day(workOrder.start_date)}</dd>
            </div>
          )}
          {day(workOrder.end_date) && (
            <div className="wo-fact">
              <dt>Ends</dt><dd>{day(workOrder.end_date)}</dd>
            </div>
          )}
          {ticket && (
            <div className="wo-fact">
              <dt>Ticket</dt>
              <dd>
                {/* This was the raw primary key - "t-1" - under a label
                    reading TICKET ID, which told nobody anything and could
                    not be clicked. */}
                <a href={`/tickets/${ticket.id}`}>
                  {ticket.ticket_number || ticket.id} · {ticket.title}
                </a>
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* Logging hours had a section to itself - a heading and a card, 157px
          of page, holding one button. It is inside the days it adds to now. */}
      {/* Open whenever hours matter: there are some to read, or there are
          none and logging the first is the thing to do next. Only a finished
          job with nothing against it starts shut. */}
      <Fold
        name="Hours logged"
        count={hoursList.length}
        open={hoursList.length > 0 || workOrder.status !== 'completed'}
      >
        {workOrder.status !== 'completed' && (
          <HoursLogger
            workOrderId={workOrder.id}
            allocatedHours={workOrder.allocated_hours}
            loggedHours={loggedHours}
            onHoursLogged={handleHoursLogged}
          />
        )}
        {hoursList.length === 0 ? (
          <p className="wo-empty">No hours logged against this job yet.</p>
        ) : (
          <>
            <div className="wo-log">
              {hoursList.map((entry) => (
                <div className="wo-log-row" key={entry.id}>
                  <span className="wo-log-when">{day(entry.date_worked)}</span>
                  <span className="wo-log-what">
                    {entry.description || <em>No note</em>}
                    {entry.logged_by_name && (
                      <span className="wo-log-who"> — {entry.logged_by_name}</span>
                    )}
                  </span>
                  <span className="wo-log-hours">{hours(entry.hours_logged)}</span>
                </div>
              ))}
            </div>
            {/* The page listed every day and never added them up, so the one
                figure anybody wants off this list had to be done by hand. */}
            <div className="wo-log-total">
              <span>Total logged</span>
              <span>{hours(loggedHours)}</span>
            </div>
          </>
        )}
      </Fold>

      {ticket && (
        <Fold name="Linked ticket">
          <dl className="wo-meta" style={{ marginTop: 0, paddingTop: 0, borderTop: 0 }}>
            <div className="wo-fact">
              <dt>Ticket</dt>
              <dd><a href={`/tickets/${ticket.id}`}>{ticket.ticket_number || ticket.id}</a></dd>
            </div>
            <div className="wo-fact">
              <dt>Status</dt>
              <dd>{ticket.status_name || statusWord(ticket.status)}</dd>
            </div>
            <div className="wo-fact">
              <dt>Category</dt>
              <dd>{ticket.category_name || ticket.category || '—'}</dd>
            </div>
          </dl>
          {ticket.description && <p className="wo-empty">{ticket.description}</p>}
        </Fold>
      )}

      {/* Reimbursements, where Gordon went looking for them.
          //
          They were built in October and lived only on the contractor's own
          ticket page, which is reachable with a portal token and nothing else -
          so from here, and from the staff ticket view, the feature looked
          absent. The same component serves all three.

          Claimable by the contractor who holds the work order, because they
          are the person out of pocket; an administrator opening the same page
          sees what has been claimed without being offered a form to claim on
          somebody else's behalf. */}
      {ticket && (
        <Fold
          name="Expenses"
          open={!isAdmin}
        >
          <TicketExpenses
            ticketId={ticket.id}
            auth={auth}
            // Was `!isAdmin && ...`, which left an administrator with no way
            // to record an expense anywhere in the application. The person out
            // of pocket is usually the contractor, but the person holding the
            // receipt is sometimes whoever it was handed to.
            canClaim={workOrder.status !== 'completed'
                      && workOrder.status !== 'cancelled'}
          />
        </Fold>
      )}

      {/* When somebody is going to look at it, and the invitation for it.
          Open by default: an appointment is the kind of thing somebody opens
          this page to check, and a fold they have to find first is a fold
          they will not. */}
      {ticket && (
        <Fold name="Appointments" open>
          <TicketAppointments
            ticketId={ticket.id}
            auth={auth}
            canArrange={workOrder.status !== 'completed'
                        && workOrder.status !== 'cancelled'}
          />
        </Fold>
      )}

      {/* The edit form was open on arrival, so the page led with a form rather
          than with the job. It is also only worth a section at all when there
          is something in it: for somebody who cannot edit and a job with no
          notes, this was a fold you could open onto nothing, which is the
          same empty box that was taken off Create Ticket. */}
      {(isAdmin || workOrder.status === 'draft' || workOrder.notes) && (
        <Fold name={isAdmin ? 'Edit work order' : 'Work order details'}>
          <WorkOrderForm
            workOrderId={workOrder.id}
            workOrderData={workOrder}
            onSave={handleSaveWorkOrder}
            isAdmin={isAdmin}
            isReadOnly={!isAdmin && workOrder.status !== 'draft'}
            // Somebody who can edit opens this section to edit. Anyone else
            // sees whatever is in it to read, which is the notes.
            startEditing={isAdmin || workOrder.status === 'draft'}
          />
        </Fold>
      )}
    </div>
  );
};

export default WorkOrderDetail;
